<template>
  <div :class="['language-selector', { 
    'navbar-style': isNavbar,
    'login-style': isLoginFlow,
    'register-style': isRegisterFlow
  }]">
    <v-menu offset-y :close-on-content-click="true">
      <template v-slot:activator="{ on, attrs }">
        <div 
          class="language-selector-trigger" 
          v-bind="attrs" 
          v-on="on"
          :class="{ 'mobile-trigger': isMobile }"
        >
          <Flag :code="getFlagCode(currentLocale)" :squared="false" class="flag-icon" />
          <span class="language-name">{{ getLanguageName(currentLocale) }}</span>
          <v-icon small class="dropdown-icon">fa-regular fa-chevron-down</v-icon>
        </div>
      </template>
      <v-list dense>
        <v-list-item
          v-for="(locale, i) in filteredAvailableLocales"
          :key="i"
          @click="changeLocale(locale)"
          :class="{ 'selected-locale': currentLocale === locale }"
        >
          <v-list-item-avatar size="24">
            <Flag :code="getFlagCode(locale)" :squared="false" />
          </v-list-item-avatar>
          <v-list-item-title>{{ getLanguageName(locale) }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import { setLocale } from '@/i18n';

export default {
  name: 'LanguageSelector',
  props: {
    isNavbar: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    isLoginFlow: {
      type: Boolean,
      default: false
    },
    isRegisterFlow: {
      type: Boolean,
      default: false
    },
    allowedLanguages: {
      type: Array,
      default: null // null means all languages are allowed
    }
  },
  data() {
    return {
      availableLocales: ['en', 'es', 'ko', 'th', 'pt', 'zh', 'zh-Hant', 'vi', 'ja', 'hi', 'id', 'ru','uz','ms','kk'],
      languageNames: {
        en: 'English',
        es: 'Español',
        ko: '한국어',
        th: 'ไทย',
        pt: 'Português',
        zh: '简体中文',
        'zh-Hant': '繁體中文',
        vi: 'Tiếng Việt',
        ja: '日本語',
        hi: 'हिन्दी',
        id: 'Bahasa Indonesia',
        ru: 'Русский',
        uz: "O'zbekcha",
        ms: 'Bahasa Melayu',
        kk: 'Қазақша'
      },
      flagMapping: {
        en: 'US',
        es: 'ES',
        ko: 'KR',
        th: 'TH',
        vi: 'VN',
        zh: 'CN',
        'zh-Hant': 'CN',
        pt: 'PT',
        ja: 'JP',
        hi: 'IN',
        id: 'ID',
        ru: 'RU',
        uz: "UZ",
        ms: 'MY',
        kk: 'KZ'
      }
    };
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale;
    },
    filteredAvailableLocales() {
      // If allowedLanguages prop is provided and not empty, filter the available locales
      if (this.allowedLanguages && this.allowedLanguages.length > 0) {
        return this.availableLocales.filter(locale => this.allowedLanguages.includes(locale));
      }
      // Otherwise, return all available locales
      return this.availableLocales;
    }
  },
  created() {
    // Check if it's the first visit and no language preference is set
    if (!this.$store.state.preferredLanguage) {
      this.setInitialLanguage();
    }
  },
  methods: {
    changeLocale(locale) {
      if(locale !== this.currentLocale) {
        setLocale(locale);
        this.$store.commit('setPreferredLanguage', locale);
        
        // Add a short delay before refreshing to ensure the store is updated
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    },
    getLanguageName(locale) {
      return this.languageNames[locale] || locale;
    },
    getFlagCode(locale) {
      return this.flagMapping[locale] || locale;
    },
    setInitialLanguage() {
      // Get browser language (navigator.language returns format like 'en-US', 'es-ES')
      const browserLang = navigator.language || navigator.userLanguage;
      let lang = browserLang.split('-')[0]; // Get the primary language code
      
      // Check if the detected language is in our supported languages
      const supportedLang = this.availableLocales.find(locale => 
        locale === lang || locale.startsWith(lang + '-')
      );
      
      // If supported, set it as the preferred language
      if (supportedLang) {
        this.$store.commit('setPreferredLanguage', supportedLang);
        setLocale(supportedLang);
      } else {
        // Default to English if no match
        this.$store.commit('setPreferredLanguage', 'en');
        setLocale('en');
      }
    }
  }
}
</script>

<style scoped>
.language-selector {
  display: inline-block;
}

.language-selector-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px;
  transition: background-color 0.2s;
  border-radius: 4px;
}

.language-selector-trigger:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.flag-icon {
  margin-right: 8px;
}

.language-name {
  margin-right: 4px;
  font-size: 14px;
}

.dropdown-icon {
  opacity: 0.7;
}

/* Navbar specific styling */
.navbar-style .language-selector-trigger {
  color: white;
}

.navbar-style .dropdown-icon {
  font-size: 12px;
}

/* Login/Register specific styling */
.login-style .language-selector-trigger,
.register-style .language-selector-trigger {
  padding: 6px 10px;
  background-color: rgba(0,0,0,0.05);
  border-radius: 6px;
}

.login-style .language-selector-trigger:hover,
.register-style .language-selector-trigger:hover {
  background-color: rgba(0,0,0,0.1);
}

/* Dark theme adjustments */
:deep(.theme--dark) .login-style .language-selector-trigger,
:deep(.theme--dark) .register-style .language-selector-trigger {
  background-color: rgba(255,255,255,0.1);
}

:deep(.theme--dark) .login-style .language-selector-trigger:hover,
:deep(.theme--dark) .register-style .language-selector-trigger:hover {
  background-color: rgba(255,255,255,0.2);
}

/* Mobile specific styling */
.mobile-trigger {
  padding: 8px 0;
  font-size: 16px;
}

.selected-locale {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Ensure v-menu is high enough in z-index */
:deep(.v-menu__content) {
  z-index: 1100 !important;
}
</style>
